/**
 * @file        buildRateSearchPayload.js
 * @module      utils/buildRateSearchPayload
 * @description Rebuilds the exact payload the air rate-search sends to
 *              `/ratesearch/all`, so the booking endpoints (`/booking` and
 *              `/bookingUpdate`) can carry the same keys instead of forcing the
 *              backend to re-read the query document.
 *
 *              The logic here is a faithful port of the two blocks in
 *              Newrateslist/Newratelisting.jsx that build that payload:
 *                - the `allcallpayloaddata` object + `transformData` helper
 *                - the post-processing inside `getALLFlightRateSearch`
 *                  (pharmaDtls, temperature -> special-handling codes,
 *                  iataCass, Quote_status/manualQuoteOnly, shipmentTotal.type)
 *
 *              Nothing here reads redux or props directly — it's a pure
 *              function of the stored search form data, so it can be dropped
 *              into any booking payload and reasoned about in isolation.
 *
 * @author      rahul.kumar@team.sgate.in
 * @date        2026-08-19
 */

/** Ocean searches share this redux slice but use a completely different shape. */
const OCEAN_CARGO_TYPE = "Ocean Cargo";

/**
 * The frozen option on the form's temperature toggle (labelled "-10°C" — see
 * the range list in NewsearchForm.jsx). Unlike the banded options it is a
 * single set-point, not a from/to window: the form still records
 * lower: -10 / upper: -30 so the chip can render, but the airline pharma block
 * must go out with every temperature field explicitly null.
 */
const FROZEN_TEMPERATURE_RANGE = "-10";

/**
 * Coerces a temperature bound to the value the pharma block should carry.
 * The form initialises bounds to "" and they can be absent entirely, both of
 * which must serialise as an explicit `null` rather than vanishing from the
 * JSON body (an `undefined` value is dropped by JSON.stringify, which would
 * remove the key the airline API expects to see).
 *
 * @param {number|string|null|undefined} value - a stored temperature bound
 * @returns {number|string|null} the bound, or null when not meaningfully set
 */
function normalizeTemperatureBound(value) {
  return value === "" || value === undefined || value === null ? null : value;
}

/**
 * Builds the airline `passivePharmaDtl` block from a payload's temperature
 * fields.
 *
 * Exported and shared deliberately: the rate-search payload is assembled in
 * three places (this module, Newratelisting.jsx's getALLFlightRateSearch, and
 * NewRateAvailableList.js's getALLFlightRateSearch) and the frozen set-point
 * rule has to be identical in all of them, so they all call this rather than
 * inlining the object.
 *
 * @param {object} source - anything carrying temperatureRange /
 *   uppertempbound / lowertempbound at its top level
 * @returns {object} the passivePharmaDtl block, temperature fields normalised
 */
export function buildPassivePharmaDtl(source) {
  // The frozen "-10" range is a set-point, not a from/to window, so its stored
  // -10/-30 bounds must NOT go out — every temperature field is nulled instead.
  const isFrozenSetPoint =
    source?.temperatureRange === FROZEN_TEMPERATURE_RANGE;
  const tempTo = isFrozenSetPoint
    ? null
    : normalizeTemperatureBound(source?.uppertempbound);
  const tempFrom = isFrozenSetPoint
    ? null
    : normalizeTemperatureBound(source?.lowertempbound);
  return {
    tnsptTmpTo: tempTo,
    tnsptTmpFrm: tempFrom,
    stwgTmpTo: tempTo,
    stwgTmpFrm: tempFrom,
    requestType: "Passive",
    prdTmpTo: tempTo,
    prdTmpFrm: tempFrom,
    instructions: "",
  };
}

/**
 * Keys this builder deliberately NEVER emits, because the booking payloads own
 * them and the values disagree:
 *
 *   requestType        the search payload's value can legitimately be "2" while
 *                      the booking is a create ("1") — spreading it flat would
 *                      turn a create into an update
 *   queryId            booking resolves this from the socket store first
 *   bookingId          booking has a better resolution chain
 *                      (socketBookingId ?? selectedItem ?? fetched fallback)
 *   airwaybillNumber   booking sends `airWayBillNumber` (capital W) / `mblNumber`
 *   booking_status     backend-owned for air; only ocean writes it client-side
 *
 * Because none of them are emitted, the result of this function is safe to
 * spread flat at the root of a booking payload ahead of that payload's own keys.
 */

/**
 * Expands each package's `handling` label into the boolean pair the backend
 * expects. Identical to `transformData` in Newratelisting.jsx.
 *
 * @param {object} data - a payload object containing packagingDetails
 * @returns {object} a new payload with packages carrying turnable/stackable
 */
function transformData(data) {
  const updatedPackages = data?.packagingDetails?.packagingType?.packages?.map(
    (pkg) => {
      const handling = pkg?.handling;
      const newPkg = { ...pkg };
      delete newPkg?.handling;
      if (handling === "Tiltable and Stackable") {
        newPkg.turnable = true;
        newPkg.stackable = true;
      } else if (handling === "Tiltable and Non-stackable") {
        newPkg.turnable = true;
        newPkg.stackable = false;
      } else if (handling === "Non-Tiltable and Stackable") {
        newPkg.turnable = false;
        newPkg.stackable = true;
      } else if (handling === "Non Tiltable and Non stackable") {
        newPkg.turnable = false;
        newPkg.stackable = false;
      }
      return newPkg;
    },
  );
  return {
    ...data,
    packagingDetails: {
      ...data?.packagingDetails,
      packagingType: {
        ...data?.packagingDetails?.packagingType,
        packages: updatedPackages,
      },
    },
  };
}

/**
 * Appends the temperature-driven special-handling code to goodsDetails.
 * 2-8 -> CRT (chilled), 15-25 -> COL (controlled ambient), 2-25 -> ERT (extended).
 *
 * @param {object} payload - payload carrying goodsDetails + temperatureRange
 * @returns {object} payload with the code appended, or the input untouched
 */
function withTemperatureHandlingCode(payload) {
  const tempRange = payload?.temperatureRange;
  const code =
    tempRange === "2-8"
      ? "COL"
      : tempRange === "15-25"
        ? "CRT"
        : tempRange === "2-25"
          ? "ERT"
          : tempRange==="-10"?"PER": null;
  if (!code) return payload;
  return {
    ...payload,
    goodsDetails: {
      ...payload?.goodsDetails,
      specialHandlingCodes: [
        ...(payload?.goodsDetails?.specialHandlingCodes || []),
        code,
      ],
    },
  };
}

/**
 * Decides the pharmaDtls block. Mirrors the branch in getALLFlightRateSearch
 * exactly — the ordering of these conditions matters, so it is preserved
 * verbatim rather than simplified.
 *
 * @param {object} payload - the assembled search payload
 * @param {object} productDetails - the selected commodity's product details
 * @returns {object} payload with pharmaDtls attached where applicable
 */
function withPharmaDetails(payload, productDetails) {
  const shc = payload?.goodsDetails?.specialHandlingCodes || [];
  const condition = shc.includes("PEP") || shc.includes("PER");
  const condition2 = productDetails?.productCode === "QFR";
  const condition3 = payload?.temperatureRange || false;
  const commodityGroup = payload?.goodsDetails?.commodityGroup?.toLowerCase();
  const isPerishableTempControlled =
    commodityGroup === "perishables" && !!condition3;
  const isPerishablesNoTemp = commodityGroup === "perishables no temp";
  const hasTempBounds =
    payload?.uppertempbound != null &&
    payload?.uppertempbound !== "" &&
    payload?.lowertempbound != null &&
    payload?.lowertempbound !== "";

  const carePayload = {
    ...payload,
    pharmaDtls: { pharmaReqType: "Care", isPassive: false },
  };
  // Note the temperature nulling inside buildPassivePharmaDtl affects only the
  // values emitted — the branch selection below still reads the raw bounds via
  // hasTempBounds, so which pharma block gets chosen is unchanged.
  const passivePayload = {
    ...payload,
    pharmaDtls: {
      pharmaReqType: "Passive",
      passivePharmaDtl: buildPassivePharmaDtl(payload),
      isPassive: true,
    },
  };

  if (
    (condition === true && condition2 === true && condition3) ||
    (condition === false && condition2 === true)
  ) {
    return passivePayload;
  }
  if (condition === true && condition2 === true && condition3 === false) {
    return carePayload;
  }
  if (isPerishableTempControlled) {
    return passivePayload;
  }
  if (isPerishablesNoTemp && hasTempBounds) {
    return passivePayload;
  }
  return payload;
}

/**
 * Builds the flat rate-search payload for a booking request.
 *
 * @param {object} newsearchformdata - the stored search form state from redux
 *   (`state.newsearchform.newsearchformdata`)
 * @param {object} [options]
 * @param {Array}  [options.originairports] - origin airport list from
 *   `searchratestate`, used only for the `iataCass` fallback lookup
 * @param {string} [options.requestType] - "1" or "2"; drives the
 *   airlinePrefix/ratesFrom keys. Defaults to the stored search's own value.
 *   NOTE: this decides which keys are emitted, it is never emitted itself.
 * @param {boolean} [options.isManualQuoteOnly] - defaults to the stored
 *   `isManualQuoted` flag. The `?manualQuote=true` URL param that also feeds
 *   this on the results page does not exist on the booking routes.
 * @returns {object} the flat payload, or `{}` when there is nothing to build
 *   (missing search data, or an ocean search — which uses a different shape
 *   and different endpoints entirely)
 */
export function buildRateSearchPayload(newsearchformdata, options = {}) {
  const {
    originairports,
    requestType: requestTypeOption,
    isManualQuoteOnly: isManualQuoteOnlyOption,
  } = options;

  const searchquery = newsearchformdata?.searchquery;
  if (!newsearchformdata || !searchquery) return {};

  const {
    productDetails,
    lowertempbound,
    temperatureRange,
    uppertempbound,
    flightitinaries,
    ratesFrom,
    commodityGroup,
    commodityCode,
    commodity_description,
    isManualQuoted,
  } = newsearchformdata;

  const {
    originAirport,
    originPort,
    destinationAirport,
    destinationPort,
    goodsDetails,
    packagingDetails,
    paymentTerms,
    cargoType,
    cass,
    preferred_airline,
    quoteExpiry,
  } = searchquery;

  // Ocean bookings go to ratesearch/create-ocean-booking and
  // kam/ocean-booking/confirm, never to /booking — and an ocean search stores
  // originPort/containerDetails under names this builder doesn't read, so
  // running it here would emit a wall of `undefined`.
  if (cargoType === OCEAN_CARGO_TYPE) return {};

  const requestType = requestTypeOption ?? newsearchformdata?.requestType;
  const isManualQuoteOnly = isManualQuoteOnlyOption ?? !!isManualQuoted;

  const base = {
    cargoType,
    originAirport: originAirport || originPort,
    destinationAirport: destinationAirport || destinationPort,
    goodsDetails: {
      cargoreadydate: goodsDetails?.cargoreadydate,
      commodityId: productDetails?.commodityId || goodsDetails?.commodityId,
      commodityGroup:
        commodityGroup || productDetails?.commodityGroup || "General Cargo",
      commodityCode: commodityCode || goodsDetails?.commodityCode || "",
      specialHandlingCodes:
        productDetails?.specialHandlingCodes ||
        goodsDetails?.specialHandlingCodes,
      commodity_description:
        commodity_description || productDetails?.natureOfGoods || "",
    },
    packagingDetails,
    paymentTerms,
    lowertempbound,
    temperatureRange,
    uppertempbound,
  };

  // Only include preferred_airline when the user actually picked carriers —
  // otherwise the key is omitted entirely, same as the search payload.
  if (Array.isArray(preferred_airline) && preferred_airline.length > 0) {
    base.preferred_airline = preferred_airline;
  }
  if (quoteExpiry) {
    base.quoteExpiry = quoteExpiry;
  }
  // Re-quote/update requests tell the backend which carrier the existing
  // booking is on so it can price against the same source.
  if (String(requestType) === "2") {
    base.airlinePrefix = flightitinaries?.[0]?.airlinePrefix || null;
    base.ratesFrom = ratesFrom || null;
  }

  let payload = transformData(base);
  payload = withPharmaDetails(payload, productDetails);
  payload = withTemperatureHandlingCode(payload);

  // IATA CASS code: the search form stores it on the query when known,
  // otherwise it's looked up from the origin airport list by display label.
  const selectedOriginAirport = (originAirport || originPort)?.originAirport;
  const originAirportObj = Array.isArray(originairports)
    ? originairports.find(
        (item) => `${item.cityName} - (${item.code})` === selectedOriginAirport,
      )
    : null;
  const iataCass = cass || originAirportObj?.cass || "";

  return {
    ...payload,
    iataCass,
    ...(isManualQuoted
      ? { Quote_status: searchquery?.Quote_status || "Manual Quoted" }
      : {}),
    ...(isManualQuoteOnly ? { manualQuoteOnly: true } : {}),
    // shipmentTotal.type carries the weight mode (1 = KG/UNIT, 2 = TOTAL);
    // the form keeps it on packagingType.weightMode, so mirror it here exactly
    // as the search payload does.
    packagingDetails: {
      ...payload?.packagingDetails,
      shipmentTotal: {
        ...payload?.packagingDetails?.shipmentTotal,
        type: payload?.packagingDetails?.packagingType?.weightMode,
      },
    },
  };
}

export default buildRateSearchPayload;
